import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LineChart, Line, Legend } from "recharts";
import { pathColor } from "@/lib/epidemic";

export const Panel = ({ title, children, testId, className = "" }) => (
  <div className={`bg-white border border-stone-200 rounded-lg p-6 ${className}`} data-testid={testId}>
    <h3 className="font-heading text-base font-bold text-stone-900">{title}</h3>
    <div className="mt-4">{children}</div>
  </div>
);

const axis = { tickLine: false, axisLine: false, fontSize: 12, stroke: "#78716C" };
const tip = (label) => ({ formatter: (v) => [v, label], cursor: { fill: "#F5F5F4" } });

export const AgeChart = ({ data }) => (
  <div className="h-64"><ResponsiveContainer>
    <BarChart data={data}>
      <CartesianGrid vertical={false} stroke="#E7E5E4" />
      <XAxis dataKey="name" {...axis} interval={0} fontSize={11} />
      <YAxis allowDecimals={false} width={30} {...axis} />
      <Tooltip {...tip("Cas")} />
      <Bar dataKey="count" fill="#C2410C" radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer></div>
);

export const PathologyChart = ({ data }) => (
  <div style={{ height: Math.max(200, data.length * 34) }}><ResponsiveContainer>
    <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
      <XAxis type="number" allowDecimals={false} {...axis} />
      <YAxis type="category" dataKey="name" width={110} {...axis} />
      <Tooltip {...tip("Cas")} />
      <Bar dataKey="count" radius={[0, 4, 4, 0]}>{data.map((d) => <Cell key={d.name} fill={pathColor(d.name)} />)}</Bar>
    </BarChart>
  </ResponsiveContainer></div>
);

export const CityChart = ({ data, onSelect }) => (
  <div style={{ height: Math.max(200, data.length * 30) }}><ResponsiveContainer>
    <BarChart data={data.map((d) => ({ name: d.city, count: d.count }))} layout="vertical" margin={{ left: 20 }}>
      <XAxis type="number" allowDecimals={false} {...axis} />
      <YAxis type="category" dataKey="name" width={110} {...axis} />
      <Tooltip {...tip("Cas")} />
      <Bar dataKey="count" fill="#0F766E" radius={[0, 4, 4, 0]} onClick={(d) => onSelect?.(d.name)} className="cursor-pointer" />
    </BarChart>
  </ResponsiveContainer></div>
);

const MONTHS = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
const wk = (s) => `${parseInt(s.slice(8), 10)} ${MONTHS[parseInt(s.slice(5, 7), 10) - 1]}`;

export const Timeline = ({ data }) => (
  <div className="h-64"><ResponsiveContainer>
    <LineChart data={data.map((d) => ({ ...d, label: wk(d.week) }))}>
      <CartesianGrid vertical={false} stroke="#E7E5E4" />
      <XAxis dataKey="label" {...axis} />
      <YAxis allowDecimals={false} width={30} {...axis} />
      <Tooltip />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Line type="monotone" dataKey="total" name="Tous les cas" stroke="#C2410C" strokeWidth={2} dot={false} />
      {data.some((d) => d.confirme != null) && <Line type="monotone" dataKey="confirme" name="Confirmés" stroke="#0F766E" strokeWidth={2} dot={false} />}
    </LineChart>
  </ResponsiveContainer></div>
);

export const SexSplit = ({ data }) => {
  const total = data.F + data.M || 1;
  return (
    <div data-testid="epidemic-sex-split">
      <div className="flex h-3 rounded overflow-hidden bg-stone-100">
        <div className="bg-[#C2410C]" style={{ width: `${(data.F / total) * 100}%` }} />
        <div className="bg-[#0F766E]" style={{ width: `${(data.M / total) * 100}%` }} />
      </div>
      <div className="flex justify-between text-sm mt-2">
        <span className="text-stone-700">Filles <b>{data.F}</b> ({Math.round((data.F / total) * 100)}%)</span>
        <span className="text-stone-700">Garçons <b>{data.M}</b> ({Math.round((data.M / total) * 100)}%)</span>
      </div>
    </div>
  );
};
